import { useAuth } from '@contexts/AuthContext';
import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import { useScrollOverflow } from '@hooks/useScrollOverflow';
import { DEFAULT_PRESET_ID, MAX_CUSTOM_PRESETS } from '@parlay-picker/shared/configPreset';
import { usePresetsStore } from '@stores/presetsStore';
import { Bookmark, Pencil, Plus, Trash2, X } from 'lucide-react';
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';

export function PresetSelector(): ReactElement | null {
  const { authFetch } = useAuth();
  const { presets, activePresetId, switchPreset, createPreset, deletePreset, renamePreset } =
    usePresetsStore();

  const currentLeagueKey = useCurrentLeagueKey();

  // Filter presets: Default (universal) + presets tagged for current league
  const leaguePresets = useMemo(
    () => presets.filter((p) => p.id === DEFAULT_PRESET_ID || p.league === currentLeagueKey),
    [presets, currentLeagueKey],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const renameRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { canScrollRight } = useScrollOverflow(scrollRef);

  useEffect(() => {
    if (showSaveInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSaveInput]);

  useEffect(() => {
    if (renameId && renameRef.current) {
      renameRef.current.focus();
      renameRef.current.select();
    }
  }, [renameId]);

  const handleCreate = async (): Promise<void> => {
    if (!presetName.trim()) return;
    setIsSaving(true);
    setSaveError(null);
    const result = await createPreset(presetName.trim(), authFetch, currentLeagueKey);
    setIsSaving(false);
    if (result.success) {
      setPresetName('');
      setShowSaveInput(false);
    } else {
      setSaveError(result.error ?? 'Failed to save');
    }
  };

  const handleDelete = async (presetId: string): Promise<void> => {
    await deletePreset(presetId, authFetch);
    setConfirmDeleteId(null);
  };

  const handleRenameSubmit = async (): Promise<void> => {
    if (!renameId || !renameName.trim()) return;
    await renamePreset(renameId, renameName.trim(), authFetch);
    setRenameId(null);
    setRenameName('');
  };

  const customCount = leaguePresets.filter((p) => p.id !== DEFAULT_PRESET_ID).length;
  const canCreateMore =
    presets.filter((p) => p.id !== DEFAULT_PRESET_ID).length < MAX_CUSTOM_PRESETS;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Bookmark className="w-3.5 h-3.5 text-theme-text-tertiary" />
          <span className="text-xs font-medium text-theme-text-secondary">Presets</span>
        </div>
        {customCount > 0 && (
          <span className="text-xs text-theme-text-tertiary">
            {customCount}/{MAX_CUSTOM_PRESETS}
          </span>
        )}
      </div>

      {/* Preset chips */}
      <div className="relative mb-2">
        {/* Right fade hint when content overflows */}
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-theme-surface to-transparent z-10 pointer-events-none rounded-r" />
        )}
        <div ref={scrollRef} className="flex gap-1.5 pb-0.5 overflow-x-auto scrollbar-none">
          {/* Create new preset — at start */}
          {canCreateMore && !showSaveInput && (
            <button
              type="button"
              onClick={() => setShowSaveInput(true)}
              className="shrink-0 text-xs px-2.5 py-1 rounded-full border border-dashed border-theme-border text-theme-text-tertiary hover:text-theme-primary hover:border-theme-primary/50 transition flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              New Preset
            </button>
          )}
          {[...leaguePresets]
            .sort((a, b) => {
              if (a.id === activePresetId) return -1;
              if (b.id === activePresetId) return 1;
              return 0;
            })
            .map((preset) => {
              const isDefault = preset.id === DEFAULT_PRESET_ID;
              const isActive = activePresetId === preset.id;

              if (renameId === preset.id) {
                return (
                  <input
                    key={preset.id}
                    ref={renameRef}
                    type="text"
                    value={renameName}
                    onChange={(e) => setRenameName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameSubmit();
                      if (e.key === 'Escape') {
                        setRenameId(null);
                        setRenameName('');
                      }
                    }}
                    onBlur={handleRenameSubmit}
                    maxLength={30}
                    className="shrink-0 text-xs px-2.5 py-1 rounded-full bg-theme-bg border border-theme-primary text-theme-text focus:outline-none w-28"
                  />
                );
              }

              return (
                <div key={preset.id} className="shrink-0 relative group/preset">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isActive) switchPreset(preset.id);
                    }}
                    className={`relative text-xs px-2.5 py-1 rounded-full transition-all ${
                      isActive
                        ? 'bg-theme-primary-fill text-theme-on-primary-fill ring-1 ring-theme-primary/50'
                        : 'bg-theme-surface-hover text-theme-text-secondary hover:text-theme-text hover:bg-theme-border'
                    }`}
                  >
                    {/* Label — fades on hover for custom presets to reveal actions */}
                    <span
                      className={
                        !isDefault ? 'group-hover/preset:opacity-30 transition-opacity' : ''
                      }
                    >
                      {preset.name}
                    </span>
                    {/* Action icons overlaid on hover */}
                    {!isDefault && (
                      <span className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover/preset:opacity-100 transition-opacity">
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenameId(preset.id);
                            setRenameName(preset.name);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              setRenameId(preset.id);
                              setRenameName(preset.name);
                            }
                          }}
                          className={`p-0.5 rounded transition ${
                            isActive
                              ? 'hover:bg-white/20 text-theme-on-primary-fill'
                              : 'hover:bg-theme-border text-theme-text'
                          }`}
                          title="Rename"
                        >
                          <Pencil className="w-3 h-3" />
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(preset.id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              setConfirmDeleteId(preset.id);
                            }
                          }}
                          className={`p-0.5 rounded transition ${
                            isActive
                              ? 'hover:bg-red-500/30 text-theme-on-primary-fill'
                              : 'hover:bg-theme-danger/15 text-theme-danger'
                          }`}
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </span>
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
        </div>
      </div>

      {/* Delete confirmation — below the chip row */}
      {confirmDeleteId &&
        (() => {
          const preset = leaguePresets.find((p) => p.id === confirmDeleteId);
          if (!preset) return null;
          return (
            <div className="flex items-center gap-2 text-xs mb-1">
              <span className="text-theme-danger">Delete &ldquo;{preset.name}&rdquo;?</span>
              <button
                type="button"
                onClick={() => handleDelete(preset.id)}
                className="px-2.5 py-0.5 rounded-full bg-theme-danger text-white hover:bg-theme-danger/80 transition"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                className="px-2.5 py-0.5 rounded-full bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-border transition"
              >
                Cancel
              </button>
            </div>
          );
        })()}

      {/* Create input */}
      {showSaveInput && (
        <div className="flex gap-1.5 items-center">
          <input
            ref={inputRef}
            type="text"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') {
                setShowSaveInput(false);
                setPresetName('');
                setSaveError(null);
              }
            }}
            placeholder="Preset name..."
            maxLength={30}
            className="flex-1 text-xs px-2 py-1 bg-theme-bg border border-theme-border rounded-lg text-theme-text placeholder:text-theme-text-tertiary focus:outline-none focus:border-theme-primary"
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={isSaving || !presetName.trim()}
            className="text-xs px-2 py-1 bg-theme-primary-fill text-theme-on-primary-fill rounded-lg hover:bg-theme-primary-fill-hover disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isSaving ? '...' : 'Create'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowSaveInput(false);
              setPresetName('');
              setSaveError(null);
            }}
            className="text-xs px-1.5 py-1 text-theme-text-tertiary hover:text-theme-text-secondary transition"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {saveError && <p className="text-xs text-theme-danger mt-1">{saveError}</p>}
    </div>
  );
}
